/**
 * Framework-agnostic route guard decisions.
 *
 * Guards return decision objects — they do NOT perform navigation or throw.
 * The calling framework layer (TanStack Router `beforeLoad`, middleware, etc.)
 * is responsible for acting on the decision.
 *
 * This separation keeps guard logic unit-testable without a router or browser.
 *
 * Security contract (from master prompt §11 and §13):
 * - UI guards are a usability layer. The backend is the final security boundary.
 * - Always handle 401 (→ /login?next=), 403, and tenant-scoped 404 responses.
 * - A 404 from a tenant-scoped resource must never reveal cross-tenant existence.
 */

import type { Membership } from '@hostesa/api-client';
import type { Permission } from './permissions';
import { hasPermission } from './permissions';
import type { Scope } from './scope';
import { hasScope } from './scope';

// ---------------------------------------------------------------------------
// Decision types
// ---------------------------------------------------------------------------

export type SessionDecision = { ok: true } | { redirectTo: string };
export type ScopeDecision = { ok: true } | { forbidden: true };
export type PermissionDecision = { ok: true } | { forbidden: true };

// ---------------------------------------------------------------------------
// Core guards
// ---------------------------------------------------------------------------

/**
 * Checks whether a session token is present.
 *
 * Returns `{ ok: true }` when authenticated, or a redirect to `/login?next=`
 * when the token is absent. Use in route guards before data loads.
 *
 * Pass `currentPath` (e.g. `/tenants/acme/hotels/main/calls`) so the user is
 * returned to the intended page after login.
 */
export function requireSession(
  token: string | null | undefined,
  currentPath?: string,
): SessionDecision {
  if (token) return { ok: true };
  const next = currentPath ? `?next=${encodeURIComponent(currentPath)}` : '';
  return { redirectTo: `/login${next}` };
}

/**
 * Checks whether the given membership covers the requested scope.
 *
 * Returns `{ ok: true }` when the membership grants access, or
 * `{ forbidden: true }` when it does not. Use this before data fetches on
 * hotel/tenant-scoped routes to reject the route without making API calls.
 */
export function requireScope(membership: Membership, scope: Scope): ScopeDecision {
  if (hasScope(membership, scope)) return { ok: true };
  return { forbidden: true };
}

/**
 * Checks whether the role grants the requested permission.
 *
 * Returns `{ ok: true }` when allowed, `{ forbidden: true }` when not.
 * This is the RBAC half — combine with `requireScope` for scoped resources.
 */
export function requirePermission(
  role: Membership['role'],
  permission: Permission,
): PermissionDecision {
  if (hasPermission(role, permission)) return { ok: true };
  return { forbidden: true };
}

// ---------------------------------------------------------------------------
// Convenience helpers
// ---------------------------------------------------------------------------

/**
 * RBAC check: "does the role have this permission?"
 *
 * For UX affordances only (hide/disable). Never use this as the sole gate for
 * a mutation — the server enforces it independently.
 */
export function can(role: Membership['role'], permission: Permission): boolean {
  return hasPermission(role, permission);
}

/**
 * RBAC + ABAC check: "does this role have the permission AND does the
 * membership cover this scope?"
 *
 * Implements the invariant:
 *   `allowed = hasPermission(role, perm) && hasScope(membership, scope)`
 *
 * Use for route-level decisions and action affordances on scoped resources.
 */
export function canInScope(
  role: Membership['role'],
  membership: Membership,
  permission: Permission,
  scope: Scope,
): boolean {
  return hasPermission(role, permission) && hasScope(membership, scope);
}

// ---------------------------------------------------------------------------
// HTTP error guard helpers
// ---------------------------------------------------------------------------

/**
 * Returns the login-redirect path for a 401 response.
 *
 * The `next` param preserves the intended destination so the user can be
 * returned there after re-authentication.
 */
export function unauthorizedRedirect(currentPath: string): string {
  return `/login?next=${encodeURIComponent(currentPath)}`;
}

/**
 * Returns true when the given HTTP status and error code combination is a
 * tenant-scoped not-found (404) that must NOT reveal cross-tenant existence.
 *
 * The UI should treat this identically to a generic 404 page — never expose
 * messages like "resource belongs to another tenant".
 */
export function isTenantScopedNotFound(status: number, _code: string): boolean {
  // All 404s from scoped paths are treated as opaque. The backend is
  // responsible for ensuring it doesn't return different payloads for
  // cross-tenant vs genuinely absent resources.
  return status === 404;
}
